import styled, { css } from 'styled-components'

export interface EventCardStyledProps {
  $type?:
    | 'transaction'
    | 'rental'
    | 'permit'
    | 'sale'
    | 'primary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'info'
  $hasImage?: boolean
  $clickable?: boolean
}

const typeStyles = {
  transaction: css`
    border-left: 3px solid ${({ theme }) => theme.colors.primary};
  `,
  rental: css`
    border-left: 3px solid #7b61ff;
  `,
  permit: css`
    border-left: 3px solid ${({ theme }) => theme.colors.warning};
  `,
  sale: css`
    border-left: 3px solid ${({ theme }) => theme.colors.success};
  `,
  primary: css`
    border-left: 3px solid ${({ theme }) => theme.colors.primary};
  `,
  success: css`
    border-left: 3px solid ${({ theme }) => theme.colors.success};
  `,
  warning: css`
    border-left: 3px solid ${({ theme }) => theme.colors.warning};
  `,
  danger: css`
    border-left: 3px solid ${({ theme }) => theme.colors.danger};
  `,
  info: css`
    border-left: 3px solid #17a2b8;
  `,
}

export const Container = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<EventCardStyledProps>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.2s ease;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};

  ${({ $type }) => $type && typeStyles[$type]}

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.text.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 12px;
`

export const Title = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`

export const Body = styled.div<{ $hasImage?: boolean }>`
  ${({ $hasImage }) =>
    $hasImage
      ? css`
          display: flex;
          gap: 16px;
          align-items: flex-start;

          @media (max-width: 768px) {
            flex-direction: column-reverse;
            gap: 12px;
          }
        `
      : ''}
`

export const Content = styled.div`
  flex: 1;
  min-width: 0;
`

export const ImageContainer = styled.div`
  flex-shrink: 0;
  width: 120px;
  height: 80px;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.tertiary};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 160px;
  }
`

export const Description = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
`

export const MetaContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 8px;
`

export const Footer = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-style: italic;
`
